import 'primevue/resources/themes/aura-light-green/theme.css';
import 'primevue/resources/primevue.min.css'; /* Deprecated */
import 'primeicons/primeicons.css';

// Tailwind layers first, then legacy/global SCSS
import '@/assets/tailwind.css';
// Base legacy/global styles (fonts, legacy variables)
import '@/assets/scss/index.scss';
// PrimeVue component overrides (must come last to override theme)
import '@/assets/scss/primevue-overrides-styles.scss';
// Layer new design tokens after legacy so they can override where intended

import { createApp } from 'vue';

// Trim noisy console logs unless explicitly enabled via env
const __DEBUG_FLAG = String(import.meta.env.VITE_DEBUG_LOGS || '').toLowerCase();
const __ENABLE_DEBUG = __DEBUG_FLAG === 'true' || __DEBUG_FLAG === '1' || __DEBUG_FLAG === 'yes';
(() => {
  const original = {
    log: console.log,
    info: console.info,
    debug: console.debug,
  };
  if (!__ENABLE_DEBUG) {
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
  }
  // Allow toggling at runtime from DevTools: window.__enableDebugLogs(true|false)
   
  window.__enableDebugLogs = (enable = true) => {
    if (enable) {
      console.log = original.log;
      console.info = original.info;
      console.debug = original.debug;
    } else {
      console.log = () => {};
      console.info = () => {};
      console.debug = () => {};
    }
  };
})();

// Suppress external SSL certificate and CDN errors in console during development
if (import.meta.env.DEV) {
  // Store original console methods for filtering
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  
  // Override console.error to filter out external CDN errors
  console.error = (...args) => {
    // Safely convert args to string, handling objects
    const message = args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
    
    if (message.includes('cdninstagram.com') || 
        message.includes('scontent-') ||
        message.includes('403 (Forbidden)') ||
        message.includes('ERR_CERT_COMMON_NAME_INVALID') ||
        message.includes('ERR_CERT_AUTHORITY_INVALID') ||
        message.includes('ERR_SSL_PROTOCOL_ERROR') ||
        message.includes('sfstandard.com') ||
        message.includes('Failed to load resource') ||
        message.includes('net::ERR_') ||
        message.includes('GET https://scontent-') ||
        message.includes('content.sfstandard.com')) {
      if (__ENABLE_DEBUG) {
        console.debug(`[Suppressed Console Error] ${message}`);
      }
      return;
    }
    originalConsoleError.apply(console, args);
  };
  
  // Override console.warn to filter out external CDN warnings and Vue constructor warnings
  console.warn = (...args) => {
    // Safely convert args to string, handling objects
    const message = args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
    
    if (message.includes('cdninstagram.com') || 
        message.includes('scontent-') ||
        message.includes('403 (Forbidden)') ||
        message.includes('Property "constructor" was accessed during render but is not defined on instance') ||
        message.includes('Unhandled error during execution of app errorHandler') ||
        message.includes('Permissions policy violation') ||
        message.includes('encrypted-media is not allowed') ||
        message.includes('Encrypted Media access has been blocked')) {
      if (__ENABLE_DEBUG) {
        console.debug(`[Suppressed Console Warning] ${message}`);
      }
      return;
    }
    originalConsoleWarn.apply(console, args);
  };

  // Global resource error handling for images, scripts, etc.
  window.addEventListener('error', (event) => {
    // Check if this is an image loading error with SSL certificate issues or CDN 403s
    const target = event.target;
    const isElement = target && typeof target === 'object' && 'tagName' in target;
    if (isElement && /** @type {any} */(target).tagName === 'IMG') {
      const src = /** @type {any} */(target).src || '';
      const isExternalDomain = src.includes('://') && !src.includes(window.location.hostname);
      if (isExternalDomain && (
        event.message?.includes('ERR_CERT_COMMON_NAME_INVALID') ||
        event.message?.includes('ERR_CERT_AUTHORITY_INVALID') ||
        event.message?.includes('ERR_SSL_PROTOCOL_ERROR') ||
        event.message?.includes('403 (Forbidden)') ||
        src.includes('sfstandard.com') || // Specific to this domain having issues
        src.includes('content.sfstandard.com') ||
        src.includes('cdninstagram.com') || // Instagram CDN
        src.includes('scontent-') // Instagram content servers
      )) {
        // Prevent this error from appearing in console
        event.preventDefault();
        event.stopPropagation();
        if (__ENABLE_DEBUG) {
          console.debug(`[Suppressed] External image error for: ${src}`);
        }
        return false;
      }
    }
    
    // Check for general network resource errors
  const message = /** @type {any} */(event).message || '';
  const filename = /** @type {any} */(event).filename || '';
    
    // Suppress certificate errors and SF Standard domain errors
    if (message.includes('ERR_CERT_COMMON_NAME_INVALID') ||
        message.includes('net::ERR_CERT_') ||
        filename.includes('content.sfstandard.com') ||
        filename.includes('c.apple.news/')) {
      event.preventDefault();
      event.stopPropagation();
      if (__ENABLE_DEBUG) {
        console.debug(`[Suppressed Resource Error] ${message}`);
      }
      return false;
    }
  }, true);
  
  // Additional aggressive suppression: Monkey patch the Image constructor to handle 403s
  // Temporarily commented out due to TypeScript compilation issues
  /*
  const OriginalImage = window.Image;
  window.Image = function(...args) {
    const img = new OriginalImage(...args);
    const originalOnerror = img.onerror;
    
    img.onerror = function(e) {
      const src = this.src || '';
      if (src.includes('cdninstagram.com') || src.includes('scontent-')) {
        // Suppress Instagram CDN errors by not calling the original error handler
        if (__ENABLE_DEBUG) {
          console.debug(`[Suppressed Image Constructor Error] Instagram CDN: ${src}`);
        }
        return false;
      }
      // Call original error handler for other errors
      if (originalOnerror) {
        return originalOnerror.call(this, e);
      }
    };
    
    return img;
  };
  
  // Copy static properties from original Image constructor
  Object.setPrototypeOf(window.Image, OriginalImage);
  Object.setPrototypeOf(window.Image.prototype, OriginalImage.prototype);
  */

  // Additional console suppression for any remaining Instagram CDN messages
  const originalConsoleLog = console.log;
  console.log = function(...args) {
    const message = args.join(' ');
    if (message.includes('scontent-') || 
        message.includes('cdninstagram.com') ||
        message.includes('403 (Forbidden)')) {
      if (__ENABLE_DEBUG) {
        console.debug(`[Suppressed Console Log] ${message}`);
      }
      return;
    }
    return originalConsoleLog.apply(console, args);
  };

  // Override console.info as well
  const originalConsoleInfo = console.info;
  console.info = function(...args) {
    const message = args.join(' ');
    if (message.includes('scontent-') || 
        message.includes('cdninstagram.com') ||
        message.includes('403 (Forbidden)')) {
      if (__ENABLE_DEBUG) {
        console.debug(`[Suppressed Console Info] ${message}`);
      }
      return;
    }
    return originalConsoleInfo.apply(console, args);
  };

  // Suppress network-level errors for additional error types
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    if (typeof url === 'string') {
      // Suppress Apple News 404 errors and SF Standard cert errors
      if (url.includes('c.apple.news/') || url.includes('content.sfstandard.com')) {
        return Promise.reject(new Error('External domain blocked to prevent console errors'));
      }
      // Block Instagram CDN requests that are causing 403 errors
      if (url.includes('scontent-') || url.includes('cdninstagram.com')) {
        return Promise.reject(new Error('Instagram CDN blocked to prevent 403 console errors'));
      }
    }
    return originalFetch.apply(this, args).catch(error => {
      // Silently handle cert errors and other network failures
      if (error.message && (
        error.message.includes('ERR_CERT_COMMON_NAME_INVALID') ||
        error.message.includes('net::ERR_CERT_') ||
        error.message.includes('net::ERR_') ||
        error.message.includes('403 (Forbidden)')
      )) {
        if (__ENABLE_DEBUG) {
          console.debug(`[Suppressed Network Error] ${error.message}`);
        }
        return Promise.reject(error);
      }
      throw error;
    });
  };

  // Enhanced XMLHttpRequest interception for Instagram CDN requests
  const originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    if (typeof url === 'string' && (url.includes('scontent-') || url.includes('cdninstagram.com'))) {
      // Block Instagram CDN requests at XHR level
      const xhr = this;
      setTimeout(() => {
        if (xhr.onerror) {
          const progressEvent = Object.assign(new Event('error'), {
            lengthComputable: false,
            loaded: 0,
            total: 0
          });
          xhr.onerror(progressEvent);
        }
      }, 0);
      return;
    }
    return originalXHROpen.call(this, method, url, ...args);
  };

}

import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import Clipboard from 'vue3-clipboard';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import Vue3SocialSharingPlugin from "vue3-social-sharing";
import VueTelInput from 'vue-tel-input';
import * as Sentry from "@sentry/vue";

import BaseAvatar from '@/components/ui-components/NewBaseAvatar/index.vue';
import BaseAvatarNew from '@/components/ui-components/NewBaseAvatarNew/index.vue';
// global import ui-kit components
import BaseButton from '@/components/ui-components/NewBaseButton/index.vue';
import BaseDropdown from '@/components/ui-components/NewBaseDropdown/index.vue';
import BaseField from '@/components/ui-components/NewBaseField/index.vue';
import BaseModal from '@/components/ui-components/NewBaseModal/index.vue';
import BaseRange from '@/components/ui-components/NewBaseRange/index.vue';
import BaseRangeSelect from '@/components/ui-components/NewBaseRangeSelect/index.vue';
import BaseDropdownCodeCountry from '@/components/ui-components/NewBaseDropdownCodeCountry/index.vue';
import BaseTextarea from '@/components/ui-components/NewBaseTextarea/index.vue';
import BaseToggle from '@/components/ui-components/NewBaseToggle/index.vue';

import App from './App.vue';
import router from './router';
import store from './store';
import dougJonesAPI from '@/plugins/api.js';
import { useAPI } from '@/services/useAPI.js';
import { triggerSessionExpired } from '@/services/errorServices/sessionExpired';
import linkWrappingPlugin from '@/plugins/linkWrappingPlugin.js';

linkWrappingPlugin(store);

const app = createApp(App);
app.component("BaseButton", BaseButton);
app.component("BaseField", BaseField);
app.component("BaseTextarea", BaseTextarea);
app.component("BaseDropdown", BaseDropdown);
app.component("BaseRange", BaseRange);
app.component("BaseModal", BaseModal);
app.component("BaseToggle", BaseToggle);
app.component("BaseAvatar", BaseAvatar);
app.component("BaseAvatarNew", BaseAvatarNew);
app.component("BaseDropdownCodeCountry", BaseDropdownCodeCountry);
app.component("BaseRangeSelect", BaseRangeSelect);

// Request deduplication cache
const requestCache = new Map();
const CACHE_DURATION = 5000; // 5 seconds for deduplication

const originalFetch = window.fetch;
window.fetch = async (input, init = {}) => {
  init = init || {};

  // Only inject X-dougjones-Platform on same-origin / relative requests.
  // External URLs (e.g. firebaseinstallations.googleapis.com) must NOT receive
  // this custom header — it triggers a CORS preflight that Google's APIs reject.
  const _url = typeof input === 'string' ? input
    : (input instanceof Request ? input.url : String(input));
  const _isExternal = /^https?:\/\//i.test(_url) && !_url.includes(window.location.hostname);

  if (!_isExternal) {
    const tokenCookie = document.cookie.split('; ').find(c => c.startsWith('Token='))?.split('=')[1];
    const accessToken = localStorage.getItem('access_token');
    const authToken = accessToken || tokenCookie;
    init.headers = {
      ...(init.headers || {}),
      "Dougjones": "web",
      ...(authToken ? { "Authorization": `${authToken}` } : {}),
    };
  }
  
  // Create cache key for deduplication (only for GET requests)
  const method = init.method || 'GET';
  let cacheKey = null;
  
  if (method === 'GET' && (!init.cache || init.cache !== 'no-cache')) {
    const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : input.href);
    cacheKey = `${method}:${url}`;
    
    // Check if request is already in flight or cached
    const cachedEntry = requestCache.get(cacheKey);
    if (cachedEntry && cachedEntry.timestamp > Date.now() - CACHE_DURATION) {
      // If we have cached response data, create a new Response from it
      if (cachedEntry.data) {
        return Promise.resolve(new Response(cachedEntry.data.body, {
          status: cachedEntry.data.status,
          statusText: cachedEntry.data.statusText,
          headers: cachedEntry.data.headers
        }));
      }
      // If request is in flight, wait for it and then return a fresh Response from cached data
      if (cachedEntry.promise) {
        return cachedEntry.promise.then(() => {
          const entry = requestCache.get(cacheKey);
          if (entry && entry.data) {
            return new Response(entry.data.body, {
              status: entry.data.status,
              statusText: entry.data.statusText,
              headers: entry.data.headers
            });
          }
          // Fallback: make a new request if cache was cleared
          return originalFetch(input, init);
        });
      }
    }
  }
  
  const requestPromise = (async () => {
    try {
      const response = await originalFetch(input, init);

      // NEW: session-expired circuit breaker — catches raw fetch() callers that
      // bypass useAPI/$apiDougJones (e.g. store actions), so a dead session
      // shows exactly one popup instead of silent failures / per-call redirects.
      // /api/auth/* is excluded so sign-in/OTP failures don't trip it.
      // /api/siwf/* is excluded — 401/403 there is part of the SIWF handshake,
      // not a session-expired event.
      if (!_isExternal && _url.includes('/api/') && !_url.includes('/api/auth/') && !_url.includes('/api/siwf/')
        && (response.status === 401 || response.status === 403)) {
        triggerSessionExpired();
      }

      // If this is a cacheable GET request, store the response data
      if (cacheKey && response.ok) {
        const clonedResponse = response.clone();
        const body = await clonedResponse.text();
        const headers = {};
        clonedResponse.headers.forEach((value, key) => {
          headers[key] = value;
        });

        // Store the raw response data in cache
        requestCache.set(cacheKey, {
          data: {
            body: body,
            status: clonedResponse.status,
            statusText: clonedResponse.statusText,
            headers: headers
          },
          timestamp: Date.now()
        });
      } else if (method !== 'GET' && response.ok) {
        // A write (POST/PUT/PATCH/DELETE) succeeded — any previously cached GET
        // response is now stale (e.g. a feed fetch cached just before a new post
        // was created), so drop the whole dedup cache to force fresh network reads.
        requestCache.clear();
      }

      return response;
    } catch (error) {
      const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : input.href);
      const isInstagramCdn = url && (url.includes('cdninstagram.com') || url.includes('scontent-'));
      
      // Suppress Instagram CDN errors in development mode
      if (import.meta.env.DEV && isInstagramCdn) {
        if (__ENABLE_DEBUG) {
          console.debug(`[Suppressed] Instagram CDN fetch error: ${url} - ${error.message}`);
        }
        // Return a minimal response for Instagram CDN errors to prevent breaking the app
        return new Response(null, { status: 403, statusText: 'Forbidden' });
      }
      
      console.error('Fetch error:', {
        url: url,
        method: init.method || 'GET',
        error: error.message
      });

      // status: 0 is invalid in the Response constructor (must be 200-599) and
      // causes RangeError. Use 503 to signal a network-level failure to callers.
      return new Response(JSON.stringify({
        error: true,
        message: 'Network request failed',
        statusCode: 503
      }), {
        status: 503,
        statusText: 'Network request failed',
      });
    }
  })();
  
  // Cache the in-flight promise for deduplication (will be replaced with data once resolved)
  if (cacheKey) {
    requestCache.set(cacheKey, {
      promise: requestPromise,
      timestamp: Date.now()
    });
    
    // Clean up old cache entries periodically
    setTimeout(() => {
      const entry = requestCache.get(cacheKey);
      if (entry && Date.now() - entry.timestamp > CACHE_DURATION) {
        requestCache.delete(cacheKey);
      }
    }, CACHE_DURATION);
  }
  
  return requestPromise;
};

// Only initialize Sentry in production or staging, not in development
if (import.meta.env.MODE !== 'development' && import.meta.env.VITE_APP_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || "local",
    release: import.meta.env.VITE_APP_GIT_HASH,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: +(import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    // Session Replay
    replaysSessionSampleRate: +(
      import.meta.env.VITE_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.0
    ),
    replaysOnErrorSampleRate: +(
      import.meta.env.VITE_APP_SENTRY_REPLAYS_ERROR_SAMPLE_RATE ?? 1.0
    ),
    sendDefaultPii: true,
    ignoreErrors: [
      "ChunkLoadError", 
      "Loading chunk", 
      "Loading CSS chunk",
      "ERR_CERT_COMMON_NAME_INVALID",
      "ERR_CERT_AUTHORITY_INVALID",
      "ERR_SSL_PROTOCOL_ERROR",
      "net::ERR_CERT_COMMON_NAME_INVALID",
      "403 (Forbidden)",
      "cdninstagram.com",
      "scontent-"
    ],
    beforeSend(event, hint) {
    if (__ENABLE_DEBUG) console.debug("Sentry beforeSend", event, hint);

      // if (event.exception) {
      //   Sentry.showReportDialog({ eventId: event.event_id });
      // }

      return event;
    },
  });
  // console.log('[Sentry] Initialized in', import.meta.env.MODE, 'mode');
} else {
  // console.log('[Sentry] Disabled in development mode');
}

app.use(PrimeVue, {
  zIndex: {
    modal: 9000,      // Dialog, Sidebar
    overlay: 9100,    // OverlayPanel, Dropdown panel, MultiSelect panel
    menu: 9200,       // Menu, ContextMenu, TieredMenu
    tooltip: 9300,    // Tooltip directive
  },
});
app.directive('tooltip', Tooltip);
app.use(ToastService);
app.use(ConfirmationService);
app.use(Vue3SocialSharingPlugin);
app.use(Clipboard, { autoSetContainer: true, appendToBody: true });
app.use(VueTelInput, {
  dropdownOptions: {
    showDialCode: true,
    showSearchBox: true,
    searchBoxPlaceholder: "Search Country",
  }
});
app.use(store);
app.use(router);
app.use(dougJonesAPI);
app.provide('useAPI', useAPI);

// Set data-theme and initial scheme before mount so CSS [data-theme] selectors
// apply on first render (applyScheme in App.vue runs after components mount).
const __themePreset = import.meta.env.VITE_THEME_PRESET || 'default';
const __savedScheme = localStorage.getItem('launchpad:scheme');
const __initScheme = __savedScheme || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
document.documentElement.setAttribute('data-theme', __themePreset);
document.documentElement.setAttribute('scheme', __initScheme);

app.mount("#app");